// =============================================================================
// VandieZ admin-web -- Inbox (/inbox)
// =============================================================================
// Read-only view of the admin's own notification feed (the same one the
// mobile apps will show). Backed by GET /users/me/notifications. Each
// row is the title + body the FCM dispatcher sent, plus an event_type
// + data dict mirroring the original push payload. Tap = mark read +
// follow the `route_path` (when present).
//
// The sidebar badge polls the unread count every 30s; see useUnreadCount
// in @/lib/inbox-counts.
// =============================================================================

import {
  AlertCircle,
  Bell,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  Mail,
  MailOpen,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { inboxApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { TableSkeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type { NotificationRead } from "@/types/inbox";

const PAGE_SIZE = 50;

type Filter = "all" | "unread";

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) {return iso;}
  const seconds = Math.max(1, Math.round((Date.now() - then) / 1000));
  if (seconds < 60) {return `${seconds}s ago`;}
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {return `${minutes}m ago`;}
  const hours = Math.round(minutes / 60);
  if (hours < 24) {return `${hours}h ago`;}
  const days = Math.round(hours / 24);
  if (days < 7) {return `${days}d ago`;}
  return new Date(iso).toLocaleDateString();
}

export function InboxPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const filter = (searchParams.get("filter") as Filter | null) ?? "all";
  const offset = Number.parseInt(searchParams.get("offset") ?? "0", 10) || 0;

  const [items, setItems] = useState<NotificationRead[]>([]);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markingAll, setMarkingAll] = useState(false);

  const updateFilters = useCallback(
    (next: { filter?: string; offset?: number }): void => {
      setSearchParams(
        (cur) => {
          const p = new URLSearchParams(cur);
          for (const [k, v] of Object.entries(next)) {
            if (v === undefined || v === "" || v === "all" || v === 0) {
              p.delete(k);
            } else {
              p.set(k, String(v));
            }
          }
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const fetchPage = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (accessToken === null) {return;}
      setLoading(true);
      setError(null);
      try {
        const res = await inboxApi.list(
          accessToken,
          {
            limit: PAGE_SIZE,
            offset,
            only_unread: filter === "unread",
          },
          signal,
        );
        setItems(res.items);
        setTotal(res.total);
        setUnreadCount(res.unread_count);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {return;}
        setError(
          e instanceof ApiError ? e.problem.detail : "Couldn't load inbox.",
        );
      } finally {
        setLoading(false);
      }
    },
    [accessToken, filter, offset],
  );

  useEffect(() => {
    const c = new AbortController();
    void fetchPage(c.signal);
    return () => { c.abort(); };
  }, [fetchPage]);

  const onRowClick = async (n: NotificationRead): Promise<void> => {
    // Mark read on the server (idempotent), then follow route_path if
    // present. Local optimistic update so the UI snaps without a refetch.
    if (n.read_at === null) {
      setItems((cur) =>
        cur.map((row) =>
          row.id === n.id
            ? { ...row, read_at: new Date().toISOString() }
            : row,
        ),
      );
      setUnreadCount((c) => Math.max(0, c - 1));
      if (accessToken !== null) {
        inboxApi.markOneRead(accessToken, n.id).catch(() => {
          // Swallow — the next refresh will reconcile.
        });
      }
    }
    const route = n.data["route_path"];
    if (typeof route === "string" && route.length > 0) {
      navigate(route);
    }
  };

  const onMarkAllRead = async (): Promise<void> => {
    if (accessToken === null) {return;}
    setMarkingAll(true);
    try {
      const res = await inboxApi.markAllRead(accessToken);
      toast.success(
        res.marked_read === 0
          ? "Inbox was already empty."
          : `Marked ${res.marked_read} as read.`,
      );
      await fetchPage();
    } catch (e: unknown) {
      const msg = e instanceof ApiError ? e.problem.detail : "Couldn't mark all read.";
      toast.error(msg);
    } finally {
      setMarkingAll(false);
    }
  };

  const canPrev = offset > 0;
  const canNext = offset + items.length < total;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Inbox"
        description="Every notification this account has received. The same feed the mobile apps show under the bell icon."
        actions={
          <Button
            variant="outline"
            onClick={() => void onMarkAllRead()}
            disabled={markingAll || unreadCount === 0}
          >
            <CheckCheck className="mr-1 h-4 w-4" />
            {markingAll ? "Marking…" : `Mark all read (${unreadCount})`}
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" /> Notifications
          </CardTitle>
          <CardDescription>
            {total > 0
              ? `Showing ${offset + 1}–${offset + items.length} of ${total}. ${unreadCount} unread.`
              : "No notifications yet."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-3 flex gap-1">
            {(["all", "unread"] as Filter[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => { updateFilters({ filter: f, offset: 0 }); }}
                className={cn(
                  "rounded px-3 py-1 text-sm capitalize",
                  filter === f
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted",
                )}
              >
                {f}
                {f === "unread" && unreadCount > 0 && (
                  <span className="ml-1 text-xs">({unreadCount})</span>
                )}
              </button>
            ))}
          </div>

          {error !== null && (
            <Alert variant="destructive" className="mb-3">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading && items.length === 0 ? (
            <TableSkeleton rows={5} columns={3} />
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm">
              {filter === "unread"
                ? "Nothing unread. You're all caught up."
                : "Notifications arrive here whenever the platform sends you a push."}
            </div>
          ) : (
            <ul className="divide-y">
              {items.map((n) => {
                const unread = n.read_at === null;
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => void onRowClick(n)}
                      className={cn(
                        "w-full text-left flex items-start gap-3 px-2 py-3 hover:bg-muted/40 transition-colors",
                        unread && "bg-primary-subtle/40",
                      )}
                    >
                      <div className="mt-0.5 shrink-0">
                        {unread ? (
                          <Mail className="h-4 w-4 text-primary" />
                        ) : (
                          <MailOpen className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <span
                            className={cn(
                              "text-sm truncate",
                              unread ? "font-semibold" : "font-medium",
                            )}
                          >
                            {n.title}
                          </span>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatRelative(n.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {n.body}
                        </p>
                        {n.event_type !== null && (
                          <span className="mt-1 inline-block rounded-full bg-muted px-2 py-0.5 text-xs font-mono">
                            {n.event_type}
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="mt-4 flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              disabled={!canPrev || loading}
              onClick={() => { updateFilters({ offset: Math.max(0, offset - PAGE_SIZE) }); }}
            >
              <ChevronLeft className="mr-1 h-4 w-4" /> Previous
            </Button>
            <span className="text-xs text-muted-foreground">
              {loading ? "Loading…" : `Page ${Math.floor(offset / PAGE_SIZE) + 1}`}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={!canNext || loading}
              onClick={() => { updateFilters({ offset: offset + PAGE_SIZE }); }}
            >
              Next <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
